import React from "react";
import { useState } from "react";
import { Button } from "components/Graphs/Button";
import FlatCard from "components/UI-lib/FlatCard";
import { Input } from "components/Graphs/Input";
import yaml from "js-yaml";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "components/Graphs/Accordion";
import {
  Plus,
  Search,
  Trash2,
  Database,
  Server,
  Code,
  FileCode,
} from "lucide-react";
import { Label } from "recharts";
import { Switch } from "components/Graphs/Switch";
import { CubeIcon, EyeIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import DockerCompose from "components/Environment/Create/ContainerSelector/DockerCompose";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import Modal from "components/Modals/Modal";

export interface UIBuilderContainer {
  id: string;
  name: string;
  image: string;
  ports: string[];
  volumes: string[];
  environment: { key: string; value: string }[];
  command?: string;
}

interface ContainerConfigurationProps {
  UIBuilderContainers: UIBuilderContainer[];
  onUIBuilderContainersChange: (containers: UIBuilderContainer[]) => void;
  useExistingCompose: boolean;
  onExistingComposeToggle: (value: boolean) => void;
  selectedContainer: ContainerSelected | undefined;
  setSelectedContainer: (container: ContainerSelected) => void;
  onResetDockerCompose: () => void;
}

export const ContainerConfiguration: React.FC<ContainerConfigurationProps> = ({
  UIBuilderContainers,
  onUIBuilderContainersChange,
  useExistingCompose,
  onExistingComposeToggle,
  selectedContainer,
  setSelectedContainer,
  onResetDockerCompose,
}) => {
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);

  const addContainer = (image = "", name = "") => {
    const newContainer: UIBuilderContainer = {
      id: `container-${Date.now()}`,
      name: name || `container-${UIBuilderContainers.length + 1}`,
      image: image || "",
      ports: [],
      volumes: [],
      environment: [],
    };

    onUIBuilderContainersChange([...UIBuilderContainers, newContainer]);
  };

  const removeContainer = (id: string) => {
    const updatedContainers = UIBuilderContainers.filter(
      (container) => container.id !== id
    );
    onUIBuilderContainersChange(updatedContainers);
    if (updatedContainers.length === 0) {
      onResetDockerCompose();
    }
  };

  const updateContainer = (
    id: string,
    updates: Partial<UIBuilderContainer>
  ) => {
    onUIBuilderContainersChange(
      UIBuilderContainers.map((container) =>
        container.id === id ? { ...container, ...updates } : container
      )
    );
  };

  const addPort = (containerId: string) => {
    const container = UIBuilderContainers.find((c) => c.id === containerId);
    if (container) {
      const updatedContainer = {
        ...container,
        ports: [...container.ports, ""],
      };
      updateContainer(containerId, updatedContainer);
    }
  };

  const updatePort = (containerId: string, index: number, value: string) => {
    const container = UIBuilderContainers.find((c) => c.id === containerId);
    if (container) {
      const updatedPorts = [...container.ports];
      updatedPorts[index] = value;
      updateContainer(containerId, { ports: updatedPorts });
    }
  };

  const removePort = (containerId: string, index: number) => {
    const container = UIBuilderContainers.find((c) => c.id === containerId);
    if (container) {
      const updatedPorts = container.ports.filter((_, i) => i !== index);
      updateContainer(containerId, { ports: updatedPorts });
    }
  };

  const addVolume = (containerId: string) => {
    const container = UIBuilderContainers.find((c) => c.id === containerId);
    if (container) {
      const updatedContainer = {
        ...container,
        volumes: [...container.volumes, ""],
      };
      updateContainer(containerId, updatedContainer);
    }
  };

  const updateVolume = (containerId: string, index: number, value: string) => {
    const container = UIBuilderContainers.find((c) => c.id === containerId);
    if (container) {
      const updatedVolumes = [...container.volumes];
      updatedVolumes[index] = value;
      updateContainer(containerId, { volumes: updatedVolumes });
    }
  };

  const removeVolume = (containerId: string, index: number) => {
    const container = UIBuilderContainers.find((c) => c.id === containerId);
    if (container) {
      const updatedVolumes = container.volumes.filter((_, i) => i !== index);
      updateContainer(containerId, { volumes: updatedVolumes });
    }
  };

  const addEnvVar = (containerId: string) => {
    const container = UIBuilderContainers.find((c) => c.id === containerId);
    if (container) {
      const updatedContainer = {
        ...container,
        environment: [...container.environment, { key: "", value: "" }],
      };
      updateContainer(containerId, updatedContainer);
    }
  };

  const updateEnvVar = (
    containerId: string,
    index: number,
    key: string,
    value: string
  ) => {
    const container = UIBuilderContainers.find((c) => c.id === containerId);
    if (container) {
      const updatedEnv = [...container.environment];
      updatedEnv[index] = { key, value };
      updateContainer(containerId, { environment: updatedEnv });
    }
  };

  const removeEnvVar = (containerId: string, index: number) => {
    const container = UIBuilderContainers.find((c) => c.id === containerId);
    if (container) {
      const updatedEnv = container.environment.filter((_, i) => i !== index);
      updateContainer(containerId, { environment: updatedEnv });
    }
  };

  // Sample gallery items
  const galleryItems = [
    {
      name: "CUDA Development",
      image: "nvidia/cuda:12.0.1-devel-ubuntu22.04",
      icon: <Code />,
    },
    {
      name: "PyTorch",
      image: "nvcr.io/nvidia/pytorch:23.12-py3",
      icon: <Server />,
    },
    {
      name: "TensorFlow",
      image: "nvcr.io/nvidia/tensorflow:23.12-tf2-py3",
      icon: <Server />,
    },
    { name: "PostgreSQL", image: "postgres:15", icon: <Database /> },
    {
      name: "RAPIDS",
      image: "nvcr.io/nvidia/rapidsai/rapidsai:23.12-cuda12.0-py3.10",
      icon: <Server />,
    },
  ];

  return (
    <div>
      <Modal
        setOpen={setEditModalOpen}
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title=""
        width={"max-w-7xl"}
        hideCancel={true}
        hideSuccess={true}
        body={
          <>
            <div className="flex flex-col text-white">
              <pre className="text-xs font-mono whitespace-pre-wrap overflow-x-auto">
                {generateDockerComposeYaml(UIBuilderContainers)}
              </pre>
            </div>
          </>
        }
      />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-primary text-xl font-semibold">Docker Compose</h2>
          <p className="text-secondary">
            Build your Docker Compose file or Upload your own.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Switch
            id="use-existing"
            checked={useExistingCompose}
            onCheckedChange={(checked) => {
              onExistingComposeToggle(checked);
              onResetDockerCompose();
              setSelectedContainer({});
              onUIBuilderContainersChange([]);
            }}
          />
          <label htmlFor="use-existing" className="text-primary text-sm">
            I have an existing docker-compose.yaml file
          </label>
        </div>
      </div>

      {useExistingCompose ? (
        <DockerCompose
          containerSelected={selectedContainer}
          onSelectContainer={setSelectedContainer}
          hideCredentials={true}
          hideSaveAndContinue={true}
          onReset={() => {
            onResetDockerCompose();
          }}
        />
      ) : (
        <>
          {UIBuilderContainers.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-500 rounded-lg">
              <CubeIcon className="mx-auto h-12 w-12 text-secondary mb-3" />
              <h3 className="text-lg font-medium text-primary mb-1">
                No containers added yet
              </h3>
              <p className="text-secondary mb-4 text-sm">
                Add containers to populate your docker-compose.yaml file or
                toggle the existing docker-compose.yaml file option above.
              </p>
              <Button onClick={() => addContainer()}>
                <Plus className="mr-2 h-4 w-4" /> Add Container
              </Button>
            </div>
          ) : (
            <div className="">
              <div className="flex justify-start items-center">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setEditModalOpen(true);
                  }}
                >
                  <EyeIcon className="mr-2 h-4 w-4" />
                  View Docker Compose Yaml
                </Button>
              </div>
              <hr className="w-[100%] mb-3 mt-3 dark:border dark:border-zinc-800" />
              <div className="space-y-6">
                {UIBuilderContainers.map((container, index) => (
                  <FlatCard key={container.id} className="relative">
                    <div className="pb-2">
                      <div className="flex justify-between items-center">
                        <p>Container {index + 1}</p>
                        <Button
                          variant="secondary"
                          onClick={() => removeContainer(container.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-sm text-primary">Image</h3>
                          <Input
                            value={container.image}
                            onChange={(e) =>
                              updateContainer(container.id, {
                                image: e.target.value,
                              })
                            }
                          />
                        </div>

                        <Accordion type="single" collapsible className="w-full">
                          <AccordionItem value="ports">
                            <AccordionTrigger className="text-sm font-medium">
                              Ports
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-2">
                                {container.ports.map((port, portIndex) => (
                                  <div
                                    key={portIndex}
                                    className="flex items-center gap-2"
                                  >
                                    <Input
                                      placeholder="host:container (e.g., 8080:80)"
                                      value={port}
                                      onChange={(e) =>
                                        updatePort(
                                          container.id,
                                          portIndex,
                                          e.target.value
                                        )
                                      }
                                    />
                                    <Button
                                      variant="secondary"
                                      onClick={() =>
                                        removePort(container.id, portIndex)
                                      }
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                                <Button
                                  variant="secondary"
                                  onClick={() => addPort(container.id)}
                                >
                                  <Plus className="mr-2 h-3 w-3" /> Add Port
                                </Button>
                              </div>
                            </AccordionContent>
                          </AccordionItem>

                          <AccordionItem value="volumes">
                            <AccordionTrigger className="text-sm font-medium">
                              Volumes
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-2">
                                {container.volumes.map(
                                  (volume, volumeIndex) => (
                                    <div
                                      key={volumeIndex}
                                      className="flex items-center gap-2"
                                    >
                                      <Input
                                        placeholder="host:container (e.g., ./data:/data)"
                                        value={volume}
                                        onChange={(e) =>
                                          updateVolume(
                                            container.id,
                                            volumeIndex,
                                            e.target.value
                                          )
                                        }
                                      />
                                      <Button
                                        variant="secondary"
                                        onClick={() =>
                                          removeVolume(
                                            container.id,
                                            volumeIndex
                                          )
                                        }
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )
                                )}
                                <Button
                                  variant="secondary"
                                  onClick={() => addVolume(container.id)}
                                >
                                  <Plus className="mr-2 h-3 w-3" /> Add Volume
                                </Button>
                              </div>
                            </AccordionContent>
                          </AccordionItem>

                          <AccordionItem value="environment">
                            <AccordionTrigger className="text-sm font-medium">
                              Environment Variables
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-2">
                                {container.environment.map((env, envIndex) => (
                                  <div
                                    key={envIndex}
                                    className="flex items-center gap-2"
                                  >
                                    <Input
                                      placeholder="Key"
                                      value={env.key}
                                      onChange={(e) =>
                                        updateEnvVar(
                                          container.id,
                                          envIndex,
                                          e.target.value,
                                          env.value
                                        )
                                      }
                                      className="w-1/3"
                                    />
                                    <Input
                                      placeholder="Value"
                                      value={env.value}
                                      onChange={(e) =>
                                        updateEnvVar(
                                          container.id,
                                          envIndex,
                                          env.key,
                                          e.target.value
                                        )
                                      }
                                      className="w-2/3"
                                    />
                                    <Button
                                      variant="secondary"
                                      onClick={() =>
                                        removeEnvVar(container.id, envIndex)
                                      }
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                                <Button
                                  variant="secondary"
                                  onClick={() => addEnvVar(container.id)}
                                >
                                  <Plus className="mr-2 h-3 w-3" /> Add
                                  Environment Variable
                                </Button>
                              </div>
                            </AccordionContent>
                          </AccordionItem>

                          <AccordionItem value="command">
                            <AccordionTrigger className="text-sm font-medium">
                              Command
                            </AccordionTrigger>
                            <AccordionContent>
                              <Input
                                placeholder="e.g., python app.py"
                                value={container.command || ""}
                                onChange={(e) =>
                                  updateContainer(container.id, {
                                    command: e.target.value,
                                  })
                                }
                              />
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </div>
                    </div>
                  </FlatCard>
                ))}
              </div>
              <div className="flex justify-center mt-4">
                <Button onClick={() => addContainer()}>
                  <Plus className="mr-2 h-4 w-4" /> Add Container
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const generateDockerComposeYaml = (
  containers: UIBuilderContainer[]
): string => {
  const composeConfig: any = {
    version: "3.8",
    services: {},
  };

  containers.forEach((container) => {
    const service: any = {
      image: container.image,
    };

    // Add ports if any
    if (container.ports.length > 0) {
      service.ports = container.ports.filter((port) => port.trim() !== "");
    }

    // Add volumes if any
    if (container.volumes.length > 0) {
      service.volumes = container.volumes.filter(
        (volume) => volume.trim() !== ""
      );
    }

    // Add environment variables if any
    if (container.environment.length > 0) {
      service.environment = container.environment
        .filter((env) => env.key.trim() !== "" && env.value.trim() !== "")
        .map((env) => `${env.key}=${env.value}`);
    }

    // Add command if specified
    if (container.command?.trim()) {
      service.command = container.command;
    }

    composeConfig.services[container.name] = service;
  });

  // Convert to YAML string
  return yaml.dump(composeConfig, { lineWidth: -1 });
};
